// Header-only image dimension reader (PNG + JPEG). NO decode — reads the size
// fields straight from the file header, so it's cheap enough for a request guard.
// Used to reject an oversized tenant logo before it inflates PDF-generate CPU
// (the job-pdf 1102). Returns null when the format/size can't be determined.

export interface ImageDims {
  width: number;
  height: number;
}

/** Max logo dimension (longest side, px). The quote PDF renders the logo in a
 *  ~50pt box, so anything larger is oversampled and inflates PDF-generate CPU
 *  (the job-pdf 1102). Shared by the client downscale + the worker upload guard. */
export const LOGO_MAX_DIM = 150;

/** Read pixel dimensions from PNG or JPEG bytes via the header only. null if not
 *  a recognized/parseable PNG or JPEG. */
export function readImageDimensions(bytes: Uint8Array): ImageDims | null {
  return readPngDimensions(bytes) ?? readJpegDimensions(bytes);
}

function readPngDimensions(b: Uint8Array): ImageDims | null {
  // 8-byte signature, then IHDR: 4 len + "IHDR" + width(4) + height(4) at offset 16.
  if (b.length < 24) return null;
  const SIG = [137, 80, 78, 71, 13, 10, 26, 10];
  for (let i = 0; i < 8; i++) if (b[i] !== SIG[i]) return null;
  if (b[12] !== 0x49 || b[13] !== 0x48 || b[14] !== 0x44 || b[15] !== 0x52) return null; // "IHDR"
  const u32 = (o: number) => ((b[o]! << 24) | (b[o + 1]! << 16) | (b[o + 2]! << 8) | b[o + 3]!) >>> 0;
  return { width: u32(16), height: u32(20) };
}

function readJpegDimensions(b: Uint8Array): ImageDims | null {
  if (b.length < 4 || b[0] !== 0xff || b[1] !== 0xd8) return null; // SOI
  let i = 2;
  while (i + 9 < b.length) {
    if (b[i] !== 0xff) { i++; continue; } // skip to next marker
    const marker = b[i + 1]!;
    // SOF0..SOF15 carry dimensions (excluding the non-SOF 0xC4/0xC8/0xCC markers).
    const isSof =
      marker >= 0xc0 && marker <= 0xcf &&
      marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
    if (isSof) {
      // segment: FF marker len(2) precision(1) height(2) width(2)
      const height = (b[i + 5]! << 8) | b[i + 6]!;
      const width = (b[i + 7]! << 8) | b[i + 8]!;
      return { width, height };
    }
    if (marker === 0xd8 || marker === 0xd9 || (marker >= 0xd0 && marker <= 0xd7)) {
      i += 2; continue; // standalone markers, no length
    }
    const len = (b[i + 2]! << 8) | b[i + 3]!; // segment length includes the 2 length bytes
    if (len < 2) return null;
    i += 2 + len;
  }
  return null;
}
